








import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  getUserByIdApi,
  updateUserApi,
  resetUserPasswordApi,
  getDepartmentsApi,
  FILE_BASE_URL,
} from "../../app/api";
import { toast } from "react-toastify";
import { CalendarDays, ChevronLeft, ChevronRight, Copy, Eye, EyeOff, ImagePlus, KeyRound, RefreshCw, UploadCloud, X } from "lucide-react";
import { convertImageToWebP } from "../../utils/convertImageToWebP";

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const rawValue = value.trim();
    const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;

    const timestampMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]/);
    if (timestampMatch) {
      const parsedDate = new Date(rawValue);
      if (!Number.isNaN(parsedDate.getTime())) {
        const parts = new Intl.DateTimeFormat("en-CA", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(parsedDate);
        const year = parts.find((part) => part.type === "year")?.value;
        const month = parts.find((part) => part.type === "month")?.value;
        const day = parts.find((part) => part.type === "day")?.value;
        if (year && month && day) return `${year}-${month}-${day}`;
      }
    }

    const slashMatch = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slashMatch) {
      return `${slashMatch[3]}-${String(slashMatch[2]).padStart(2, "0")}-${String(slashMatch[1]).padStart(2, "0")}`;
    }

    const namedMonthMatch = rawValue.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
    if (namedMonthMatch) {
      const monthIndex = MONTH_LABELS.findIndex((month) =>
        month.toLowerCase().startsWith(namedMonthMatch[2].toLowerCase().slice(0, 3))
      );

      if (monthIndex >= 0) {
        return `${namedMonthMatch[3]}-${String(monthIndex + 1).padStart(2, "0")}-${String(namedMonthMatch[1]).padStart(2, "0")}`;
      }
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

const getEditableUserId = (user = {}) =>
  user.id || user.user_id || user.employee_user_id || user.employee_id || user.emp_id;

const createFormState = (user = {}) => ({
  phone_number: user.phone_number || "",
  alter_contact_name: user.alter_contact_name || "",
  alter_number: user.alter_number || "",
  department_id: user.department_id || "",
  job_title: user.job_title || "",
  dob: toDateInputValue(user.dob || user.date_of_birth || user.birth_date),
  address: user.address || "",
  biometric_id: user.biometric_id || "",
  gender: user.gender || "",
});

const toCalendarDate = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const toIsoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const formatDisplayDate = (value) => {
  const date = toCalendarDate(value);
  if (!date) return "Select date";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const generateSixCharacterPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let password = "";
  for (let index = 0; index < 6; index += 1) {
    password += chars[Math.floor(Math.random() * chars.length)];
  }
  return password;
};

/* ======================================================
   SUCCESS / FAIL MODAL (UNCHANGED)
====================================================== */
function UpdateResponseModal({ show, success, message, onClose }) {
  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-[3000]">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-[320px] text-center relative">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${success ? "bg-green-500" : "bg-red-500"
              }`}
          >
            <span className="text-white text-4xl">
              {success ? "✓" : "✕"}
            </span>
          </div>
        </div>

        <div className="mt-14">
          <h2
            className={`text-xl font-semibold ${success ? "text-green-600" : "text-red-600"
              }`}
          >
            {success ? "Success" : "Failed"}
          </h2>

          <p className="text-gray-600 mt-2 mb-6">{message}</p>

          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const ProfileImageDropzone = ({
  preview,
  file,
  isDragging,
  inputRef,
  onDrop,
  onDragOver,
  onDragLeave,
  onPick,
  onClear,
}) => (
  <div className="md:col-span-2">
    <label className="mb-2 block text-sm text-white/80">Profile Image</label>
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`group relative flex min-h-[138px] items-center gap-4 rounded-2xl border border-dashed p-4 transition ${
        isDragging
          ? "border-cyan-300 bg-cyan-300/14 shadow-[0_0_0_4px_rgba(103,232,249,0.12)]"
          : "border-white/25 bg-white/10 hover:border-cyan-200/55 hover:bg-white/14"
      }`}
    >
      <div className="relative grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-2xl border border-white/20 bg-[#12082f]/50 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
        {preview ? (
          <img src={preview} alt="Employee profile preview" className="h-full w-full object-cover" />
        ) : (
          <ImagePlus className="h-9 w-9 text-white/45" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <UploadCloud className="h-5 w-5 text-cyan-200" />
          Drag and drop one image
        </div>
        <p className="mt-1 text-xs leading-5 text-white/55">
          JPG, PNG or WebP accepted. It will upload as WebP.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="rounded-xl border border-cyan-200/30 bg-cyan-300/16 px-4 py-2 text-xs font-bold text-cyan-50 transition hover:bg-cyan-300/24"
          >
            Choose Image
          </button>
          {(preview || file) && (
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center gap-1 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white/75 transition hover:bg-white/16 hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
          {file && (
            <span className="max-w-[220px] truncate rounded-full bg-emerald-300/14 px-3 py-1 text-xs font-semibold text-emerald-100">
              {file.name}
            </span>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
      />
    </div>
  </div>
);

/* ======================================================
   EDIT USER - GLASSMORPHISM VERSION
====================================================== */
export default function EditUser({ user, onClose, onUpdated }) {
  const [form, setForm] = useState(() => createFormState(user));

  const [departments, setDepartments] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(
    user.profile_pic ? `${FILE_BASE_URL}${user.profile_pic}` : ""
  );
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const fileInputRef = useRef(null);

  const [updateModal, setUpdateModal] = useState({
    show: false,
    success: false,
    message: "",
  });

  useEffect(() => {
    getDepartmentsApi().then((res) => {
      if (res.success) setDepartments(res.data);
    });
  }, []);

  useEffect(() => {
    setForm(createFormState(user));
    setProfileImage(null);
    setProfilePreview(user.profile_pic ? `${FILE_BASE_URL}${user.profile_pic}` : "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [user]);

  useEffect(() => {
    return () => {
      if (profilePreview?.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
    };
  }, [profilePreview]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (showResetModal) {
        setShowResetModal(false);
        setNewPassword("");
        setShowNewPassword(false);
        return;
      }
      onClose();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, showResetModal]);

  const handleChange = (e) =>
    setForm({
      ...form,
      [e.target.name]:
        e.target.name === "dob" ? toDateInputValue(e.target.value) : e.target.value,
    });

  const handleProfileImage = (file) => {
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload only an image file");
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image should be less than 8MB");
      return;
    }

    if (profilePreview?.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
    setProfileImage(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const handleProfileDrop = (event) => {
    event.preventDefault();
    setIsDraggingImage(false);

    const files = Array.from(event.dataTransfer.files || []).filter((file) =>
      file.type?.startsWith("image/")
    );

    if (files.length > 1) {
      toast.error("Please upload only one image");
      return;
    }

    handleProfileImage(files[0]);
  };

  const clearProfileImage = () => {
    if (profilePreview?.startsWith("blob:")) URL.revokeObjectURL(profilePreview);
    setProfileImage(null);
    setProfilePreview(user.profile_pic ? `${FILE_BASE_URL}${user.profile_pic}` : "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const payload = profileImage
      ? new FormData()
      : {
        ...form,
        dob: form.dob ? toDateInputValue(form.dob) : null,
      };

    if (profileImage) {
      Object.entries({
        ...form,
        dob: form.dob ? toDateInputValue(form.dob) : "",
      }).forEach(([key, value]) => {
        payload.append(key, value ?? "");
      });
      payload.append(
        "profile_pic",
        await convertImageToWebP(profileImage, { quality: 0.86, maxWidth: 1200, maxHeight: 1200 })
      );
    }

    const editableUserId = getEditableUserId(user);
    if (!editableUserId) {
      setUpdateModal({
        show: true,
        success: false,
        message: "User id missing, update failed",
      });
      return;
    }

    const res = await updateUserApi(editableUserId, payload);

    if (res.success) {
      const freshUser = await getUserByIdApi(editableUserId);
      if (freshUser?.success && freshUser.data) {
        setForm(createFormState(freshUser.data));
      }
      window.dispatchEvent(new CustomEvent("uptask:employees-updated"));
      await onUpdated?.();
      setUpdateModal({
        show: true,
        success: true,
        message: "Employee updated successfully",
      });
    } else {
      setUpdateModal({
        show: true,
        success: false,
        message: res.message || "Update failed",
      });
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.trim().length !== 6) {
      return toast.error("Password must be exactly 6 characters");
    }

    const res = await resetUserPasswordApi(user.id, newPassword);
    if (res.success) {
      toast.success("Password reset successfully");
      setShowResetModal(false);
      setNewPassword("");
      setShowNewPassword(false);
    } else {
      toast.error(res.message || "Failed");
    }
  };

  const handleCopyPassword = async () => {
    if (!newPassword) return toast.error("Generate or enter a password first");
    await navigator.clipboard.writeText(newPassword);
    toast.success("Password copied");
  };

  const openResetPasswordModal = () => {
    setNewPassword("");
    setShowNewPassword(false);
    setShowResetModal(true);
  };

  return createPortal(
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-[2000]" />

      {/* Modal */}
      <div className="fixed inset-0 z-[2001] flex justify-center items-start overflow-y-auto px-4 py-10">
        <div className="relative w-full max-w-3xl rounded-2xl p-8 bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl text-white">

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 transition"
          >
            ✕
          </button>

          <h3 className="text-xl font-semibold mb-6">
            Edit Employee
          </h3>

          <form
            onSubmit={handleUpdate}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-1"
          >
            {/* Read Only */}
            <Input label="Employee ID" value={user.emp_id} disabled />
            <Input label="Email" value={user.email} disabled />
            <ProfileImageDropzone
              preview={profilePreview}
              file={profileImage}
              isDragging={isDraggingImage}
              inputRef={fileInputRef}
              onDrop={handleProfileDrop}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDraggingImage(true);
              }}
              onDragLeave={() => setIsDraggingImage(false)}
              onPick={(event) => handleProfileImage(event.target.files?.[0])}
              onClear={clearProfileImage}
            />
            <Input
              label="Full Name"
              value={`${user.first_name} ${user.last_name}`}
              disabled
            />

            {/* Editable */}
            <Input
              label="Phone"
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
            />

            <Input
              label="Alternate Contact Name"
              name="alter_contact_name"
              value={form.alter_contact_name}
              onChange={handleChange}
            />

            <Input
              label="Alternate Contact Number"
              name="alter_number"
              value={form.alter_number}
              onChange={handleChange}
            />

            <EditUserDropdown
              label="Department"
              value={form.job_title}
              placeholder="Select Department"
              options={departments.map((d) => ({
                label: d.name,
                value: d.name,
              }))}
              onChange={(value) => {
                const selectedDepartment = departments.find(
                  (department) => department.name === value
                );

                setForm({
                  ...form,
                  job_title: value,
                  department_id: selectedDepartment?.id || "",
                });
              }}
            />

            <Input
              label="Job Title"
              name="job_title"
              value={form.job_title}
              onChange={handleChange}
            />

            <StylishDatePicker
              label="DOB"
              value={form.dob}
              onChange={(value) => setForm({ ...form, dob: value })}
            />

            <Input
              label="Biometric ID"
              name="biometric_id"
              value={form.biometric_id}
              onChange={handleChange}
            />

            <EditUserDropdown
              label="Gender"
              name="gender"
              value={form.gender}
              placeholder="Select Gender"
              options={[
                { label: "Male", value: "male" },
                { label: "Female", value: "female" },
              ]}
              onChange={(value) => setForm({ ...form, gender: value })}
            />

            <Textarea
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
            />

            {/* Buttons */}
            <div className="md:col-span-2 flex justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={openResetPasswordModal}
                className="px-6 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition"
              >
                Reset Password
              </button>

              <button
                type="submit"
                className="px-6 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 transition"
              >
                Update Employee
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Reset Password Modal (Glass Styled) */}
      {showResetModal &&
        createPortal(
          <div className="fixed inset-0 bg-[#05010f]/70 backdrop-blur-md z-[3000] flex items-center justify-center px-4">
            <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/14 bg-[#130d25] p-6 text-white shadow-[0_28px_90px_rgba(0,0,0,0.62),inset_0_1px_0_rgba(255,255,255,0.10)]">
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),transparent_36%,rgba(168,85,247,0.12))]" />
              <div className="relative">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl border border-cyan-100/16 bg-cyan-300/12 text-cyan-100">
                      <KeyRound className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100/55">Admin Security</p>
                      <h3 className="mt-1 text-xl font-black">Change Password</h3>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowResetModal(false)}
                    className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-white/8 text-white/65 transition hover:bg-white/14 hover:text-white"
                    aria-label="Close reset password"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <label className="mb-2 block text-sm font-semibold text-white/72">New Password</label>
                <div className="flex overflow-hidden rounded-2xl border border-white/16 bg-white/10 focus-within:border-cyan-200/45 focus-within:ring-2 focus-within:ring-cyan-300/16">
                  <input
                    value={newPassword}
                    maxLength={6}
                    type={showNewPassword ? "text" : "password"}
                    onChange={(e) => setNewPassword(e.target.value.slice(0, 6))}
                    placeholder="6 chars"
                    className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base font-bold text-white outline-none placeholder:text-white/32"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    className="grid w-11 place-items-center text-white/58 transition hover:bg-white/8 hover:text-white"
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyPassword}
                    className="grid w-11 place-items-center text-white/58 transition hover:bg-white/8 hover:text-white"
                    aria-label="Copy password"
                  >
                    <Copy className="h-[18px] w-[18px]" />
                  </button>
                </div>
                <p className="mt-2 text-xs text-white/42">Password must be exactly 6 characters.</p>

                <button
                  type="button"
                  onClick={() => {
                    setNewPassword(generateSixCharacterPassword());
                    setShowNewPassword(true);
                  }}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-cyan-200/24 bg-cyan-300/12 px-4 py-3 text-sm font-black text-cyan-50 transition hover:bg-cyan-300/18"
                >
                  <RefreshCw className="h-4 w-4" />
                  Generate 6 Character Password
                </button>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setNewPassword("");
                    setShowNewPassword(false);
                  }}
                  className="px-5 py-2.5 bg-white/12 rounded-2xl hover:bg-white/18 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-500 rounded-2xl font-bold hover:from-violet-500 hover:to-indigo-400 transition disabled:opacity-50"
                  disabled={newPassword.length !== 6}
                >
                  Update Password
                </button>
              </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      <UpdateResponseModal
        show={updateModal.show}
        success={updateModal.success}
        message={updateModal.message}
        onClose={() => {
          setUpdateModal({ show: false, success: false, message: "" });
          if (updateModal.success) onClose();
        }}
      />
    </>,
    document.body
  );
}

/* ================= HELPERS ================= */

function StylishDatePicker({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedDate = toCalendarDate(value);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 90 }, (_, index) => currentYear - index);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const trailingDays = (7 - ((firstDay + daysInMonth) % 7)) % 7;

  const setMonth = (nextMonth) => {
    setViewDate(new Date(year, nextMonth, 1));
  };

  const setYear = (nextYear) => {
    setViewDate(new Date(Number(nextYear), month, 1));
  };

  const moveMonth = (offset) => {
    setViewDate(new Date(year, month + offset, 1));
  };

  const isSelected = (day) =>
    selectedDate &&
    selectedDate.getFullYear() === year &&
    selectedDate.getMonth() === month &&
    selectedDate.getDate() === day;

  const isToday = (day) => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  return (
    <div className="relative flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`group flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.10),0_14px_32px_rgba(0,0,0,0.14)] transition ${
          open
            ? "border-cyan-300/70 bg-cyan-300/12 ring-2 ring-cyan-300/22"
            : "border-white/25 bg-white/14 hover:border-cyan-200/45 hover:bg-white/18"
        }`}
      >
        <span className={value ? "text-sm font-semibold" : "text-sm text-white/58"}>
          {formatDisplayDate(value)}
        </span>
        <span className="grid h-8 w-8 place-items-center rounded-xl border border-white/10 bg-white/10 text-cyan-100 transition group-hover:bg-cyan-300/16">
          <CalendarDays className="h-4 w-4" />
        </span>
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close date picker"
            className="fixed inset-0 z-[2002] cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-[calc(100%+10px)] z-[5000] w-[330px] overflow-hidden rounded-3xl border border-cyan-100/20 bg-[#130d25] p-4 text-white shadow-[0_32px_110px_rgba(5,2,18,0.82),0_0_0_1px_rgba(255,255,255,0.05),inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-3xl">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-cyan-300/[0.05]" />
            <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/70 to-transparent" />
            <div className="relative mb-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => moveMonth(-1)}
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/8 text-white/78 transition hover:bg-white/14 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <div className="grid flex-1 grid-cols-[1.3fr_0.9fr] gap-2">
                <select
                  value={month}
                  onChange={(event) => setMonth(Number(event.target.value))}
                  className="h-9 rounded-xl border border-white/15 bg-[#23193b] px-3 text-sm font-bold text-white outline-none focus:border-cyan-200/60"
                >
                  {MONTH_LABELS.map((monthLabel, index) => (
                    <option key={monthLabel} value={index}>
                      {monthLabel}
                    </option>
                  ))}
                </select>
                <select
                  value={year}
                  onChange={(event) => setYear(event.target.value)}
                  className="h-9 rounded-xl border border-white/15 bg-[#23193b] px-3 text-sm font-bold text-white outline-none focus:border-cyan-200/60"
                >
                  {years.map((yearOption) => (
                    <option key={yearOption} value={yearOption}>
                      {yearOption}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={() => moveMonth(1)}
                className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/8 text-white/78 transition hover:bg-white/14 hover:text-white"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="relative grid grid-cols-7 gap-1 text-center">
              {WEEKDAY_LABELS.map((dayLabel, index) => (
                <div key={`${dayLabel}-${index}`} className="pb-1 text-[11px] font-black text-cyan-100/58">
                  {dayLabel}
                </div>
              ))}

              {Array.from({ length: firstDay }).map((_, index) => (
                <span key={`blank-start-${index}`} className="h-9" />
              ))}

              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const selected = isSelected(day);
                const today = isToday(day);

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      onChange(toIsoDate(new Date(year, month, day)));
                      setOpen(false);
                    }}
                    className={`relative grid h-9 place-items-center rounded-xl text-sm font-bold transition ${
                      selected
                        ? "bg-gradient-to-br from-cyan-300 to-violet-400 text-[#100822] shadow-[0_12px_28px_rgba(34,211,238,0.24)]"
                        : today
                          ? "border border-cyan-200/45 bg-cyan-300/12 text-cyan-50"
                          : "text-white/78 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}

              {Array.from({ length: trailingDays }).map((_, index) => (
                <span key={`blank-end-${index}`} className="h-9" />
              ))}
            </div>

            <div className="relative mt-4 flex items-center justify-between border-t border-white/10 pt-3">
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="rounded-xl px-3 py-2 text-xs font-bold text-white/60 transition hover:bg-white/8 hover:text-white"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  onChange(toIsoDate(today));
                  setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
                  setOpen(false);
                }}
                className="rounded-xl border border-cyan-200/25 bg-cyan-300/14 px-4 py-2 text-xs font-bold text-cyan-50 transition hover:bg-cyan-300/22"
              >
                Today
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <input
        {...props}
        className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
      />
    </div>
  );
}

function EditUserDropdown({ label, value, placeholder, options, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value));

  return (
    <div className="relative flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`group flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-2.5 text-left text-sm text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_24px_rgba(0,0,0,0.12)] transition ${
          open
            ? "border-cyan-300/70 bg-white/18 ring-2 ring-cyan-300/25"
            : "border-white/25 bg-white/14 hover:border-cyan-200/45 hover:bg-white/18"
        }`}
      >
        <span className={selected ? "truncate font-semibold" : "truncate text-white/55"}>
          {selected?.label || placeholder}
        </span>
        <span
          className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/10 bg-white/10 text-white/75 transition ${
            open ? "rotate-180 border-cyan-200/40 bg-cyan-300/20 text-cyan-50" : "group-hover:bg-white/20"
          }`}
        >
          ▾
        </span>
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close dropdown"
            className="fixed inset-0 z-[2002] cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-[2003] max-h-60 overflow-y-auto rounded-2xl border border-white/15 bg-[#171029]/95 p-1.5 text-white shadow-[0_24px_70px_rgba(5,2,18,0.55)] backdrop-blur-2xl">
            <div className="pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-cyan-200/60 to-transparent" />
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`relative flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                !value
                  ? "bg-cyan-400/16 text-cyan-50 ring-1 ring-cyan-200/25"
                  : "text-white/68 hover:bg-white/8 hover:text-white"
              }`}
            >
              <span className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${
                !value ? "bg-cyan-300 text-[#12082f]" : "bg-white/8 text-transparent"
              }`}>
                ✓
              </span>
              <span className="truncate">{placeholder}</span>
            </button>

            {options.map((option) => {
              const isSelected = String(option.value) === String(value);

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
                    isSelected
                      ? "bg-cyan-400/16 text-cyan-50 ring-1 ring-cyan-200/25"
                      : "text-white/78 hover:bg-white/8 hover:text-white"
                  }`}
                >
                  <span className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${
                    isSelected ? "bg-cyan-300 text-[#12082f]" : "bg-white/8 text-transparent"
                  }`}>
                    ✓
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div className="md:col-span-2 flex flex-col">
      <label className="text-sm mb-1 text-white/80">{label}</label>
      <textarea
        {...props}
        rows={3}
        className="w-full px-4 py-2 rounded-lg bg-white/20 border border-white/30 text-white resize-none focus:outline-none focus:ring-2 focus:ring-purple-400/50"
      />
    </div>
  );
}







// import { useState, useEffect } from "react";
// import { createPortal } from "react-dom";
// import {
//   updateUserApi,
//   resetUserPasswordApi,
//   getDepartmentsApi,
// } from "../../app/api";
// import { toast } from "react-toastify";

// /* ======================================================
//    SUCCESS / FAIL MODAL (UNCHANGED)
// ====================================================== */
// function UpdateResponseModal({ show, success, message, onClose }) {
//   if (!show) return null;

//   return createPortal(
//     <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[3000]">
//       <div className="bg-white p-6 rounded-2xl shadow-2xl w-[320px] text-center relative">
//         <div className="absolute -top-10 left-1/2 -translate-x-1/2">
//           <div
//             className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${
//               success ? "bg-green-500" : "bg-red-500"
//             }`}
//           >
//             <span className="text-white text-4xl">
//               {success ? "✓" : "✕"}
//             </span>
//           </div>
//         </div>

//         <div className="mt-14">
//           <h2
//             className={`text-xl font-semibold ${
//               success ? "text-green-600" : "text-red-600"
//             }`}
//           >
//             {success ? "Success" : "Failed"}
//           </h2>

//           <p className="text-gray-600 mt-2 mb-6">{message}</p>

//           <button
//             onClick={onClose}
//             className="w-full py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
//           >
//             OK
//           </button>
//         </div>
//       </div>
//     </div>,
//     document.body
//   );
// }

// /* ======================================================
//    EDIT USER (PORTAL + NEW UI)
// ====================================================== */
// export default function EditUser({ user, onClose, onUpdated }) {
//   const [form, setForm] = useState({
//     phone_number: user.phone_number || "",
//     alter_contact_name: user.alter_contact_name || "",
//     alter_number: user.alter_number || "",
//     department_id: user.department_id || "",
//     job_title: user.job_title || "",
//     dob: user.dob || "",
//     address: user.address || "",
//     biometric_id: user.biometric_id || "",
//     gender: user.gender || "",
//   });

//   const [departments, setDepartments] = useState([]);
//   const [showResetModal, setShowResetModal] = useState(false);
//   const [newPassword, setNewPassword] = useState("");

//   const [updateModal, setUpdateModal] = useState({
//     show: false,
//     success: false,
//     message: "",
//   });

//   useEffect(() => {
//     getDepartmentsApi().then((res) => {
//       if (res.success) setDepartments(res.data);
//     });
//   }, []);

//   const handleChange = (e) =>
//     setForm({ ...form, [e.target.name]: e.target.value });

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     const res = await updateUserApi(user.id, form);

//     if (res.success) {
//       setUpdateModal({
//         show: true,
//         success: true,
//         message: "Employee updated successfully",
//       });
//       onUpdated();
//     } else {
//       setUpdateModal({
//         show: true,
//         success: false,
//         message: res.message || "Update failed",
//       });
//     }
//   };

//   const handleResetPassword = async () => {
//     if (!newPassword.trim()) return toast.error("Enter password");

//     const res = await resetUserPasswordApi(user.id, newPassword);
//     if (res.success) {
//       toast.success("Password reset successfully");
//       setShowResetModal(false);
//       setNewPassword("");
//     } else {
//       toast.error(res.message || "Failed");
//     }
//   };

//   /* ================== PORTAL RENDER ================== */
//   return createPortal(
//     <>
//       {/* OVERLAY */}
//       <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[2000]" />

//       {/* MODAL */}
//       <div className="fixed inset-0 z-[2001] flex justify-center items-start overflow-y-auto px-4 py-10">
//         <div
//           className="
//             relative w-full max-w-xl
//             rounded-3xl p-6
//             bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f]
//             text-white shadow-2xl
//           "
//         >
//           {/* CLOSE */}
//           <button
//             onClick={onClose}
//             className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 transition"
//           >
//             ✕
//           </button>

//           <h3 className="text-2xl font-semibold text-center mb-6">
//             Edit Employee
//           </h3>

//           <form
//             onSubmit={handleUpdate}
//             className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
//           >
//             {/* READ ONLY */}
//             <Input label="Employee ID" value={user.emp_id} disabled />
//             <Input label="Email" value={user.email} disabled />
//             <Input
//               label="Full Name"
//               value={`${user.first_name} ${user.last_name}`}
//               disabled
//             />

//             {/* EDIT */}
//             <Input
//               label="Phone"
//               name="phone_number"
//               value={form.phone_number}
//               onChange={handleChange}
//             />

//             <Input
//               label="Alternate Contact Name"
//               name="alter_contact_name"
//               value={form.alter_contact_name}
//               onChange={handleChange}
//             />

//             <Input
//               label="Alternate Contact Number"
//               name="alter_number"
//               value={form.alter_number}
//               onChange={handleChange}
//             />

//             <Select
//               label="Department"
//               value={form.department_id}
//               onChange={(e) =>
//                 setForm({ ...form, department_id: e.target.value })
//               }
//             >
//               <option value="">Select Department</option>
//               {departments.map((d) => (
//                 <option key={d.id} value={d.id}>
//                   {d.name}
//                 </option>
//               ))}
//             </Select>

//             <Input label="Job Title" name="job_title" value={form.job_title} onChange={handleChange} />
//             <Input type="date" label="DOB" name="dob" value={form.dob} onChange={handleChange} />
//             <Input label="Biometric ID" name="biometric_id" value={form.biometric_id} onChange={handleChange} />

//             <Textarea label="Address" name="address" value={form.address} onChange={handleChange} />

//             <Select label="Gender" name="gender" value={form.gender} onChange={handleChange}>
//               <option value="">Select Gender</option>
//               <option value="male">Male</option>
//               <option value="female">Female</option>
//             </Select>

//             {/* ACTIONS */}
//             <div className="flex justify-end gap-3 pt-4">
//               <button
//                 type="button"
//                 onClick={() => setShowResetModal(true)}
//                 className="px-4 py-2 rounded-lg bg-red-500/80 hover:bg-red-500"
//               >
//                 Reset Password
//               </button>

//               <button
//                 type="submit"
//                 className="px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-500"
//               >
//                 Update
//               </button>
//             </div>
//           </form>
//         </div>
//       </div>

//       {/* RESET PASSWORD */}
//       {showResetModal &&
//         createPortal(
//           <div className="fixed inset-0 bg-black/60 z-[3000] flex items-center justify-center">
//             <div className="bg-white rounded-2xl p-6 w-[320px]">
//               <h3 className="font-semibold mb-4">New Password</h3>
//               <input
//                 value={newPassword}
//                 onChange={(e) => setNewPassword(e.target.value)}
//                 className="w-full px-3 py-2 border rounded mb-4"
//               />
//               <div className="flex justify-end gap-3">
//                 <button onClick={() => setShowResetModal(false)}>Cancel</button>
//                 <button
//                   onClick={handleResetPassword}
//                   className="bg-indigo-600 text-white px-4 py-2 rounded"
//                 >
//                   Submit
//                 </button>
//               </div>
//             </div>
//           </div>,
//           document.body
//         )}

//       <UpdateResponseModal
//         show={updateModal.show}
//         success={updateModal.success}
//         message={updateModal.message}
//         onClose={() => {
//           setUpdateModal({ show: false, success: false, message: "" });
//           if (updateModal.success) onClose();
//         }}
//       />
//     </>,
//     document.body
//   );
// }

// /* ================= HELPERS ================= */

// function Input({ label, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <input
//         {...props}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//       />
//     </div>
//   );
// }

// function Select({ label, children, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <select
//         {...props}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20"
//       >
//         {children}
//       </select>
//     </div>
//   );
// }

// function Textarea({ label, ...props }) {
//   return (
//     <div>
//       <p className="text-xs opacity-70 mb-1">{label}</p>
//       <textarea
//         {...props}
//         rows={3}
//         className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 resize-none"
//       />
//     </div>
//   );
// }
